import { LightMode } from "@/components/ui/color-mode";
import {
  BackupCheckStatus,
  CashRequestStatus,
  CheckRequestStatus,
  CounterDeliveryStatus,
  FutureContractStatus,
  ScheduledTransferStatus,
  SellOrderStatus,
  SuppliesOrderStatus,
  SuppliesRequestStatus,
  SlotRequestStatus,
  OrderStatus,
  TransferRequestStatus,
  SlotStatus,
} from "@/constants/entity-status";
import { AppColors } from "@/styles/colors";
import { Badge, Text } from "@chakra-ui/react";
import { useMemo } from "react";

interface Props {
  status?:
    | CashRequestStatus
    | CheckRequestStatus
    | TransferRequestStatus
    | ScheduledTransferStatus
    | SellOrderStatus
    | CounterDeliveryStatus
    | FutureContractStatus
    | SlotRequestStatus
    | SuppliesRequestStatus
    | SuppliesOrderStatus
    | OrderStatus
    | SlotStatus
    | BackupCheckStatus;
  noPaddingY?: boolean;
}

export const RequestStatusBadge = ({ status, noPaddingY }: Props) => {
  const { bgColor, label } = useMemo(() => {
    switch (status) {
      case CashRequestStatus.AVAILABLE:
      case CheckRequestStatus.AVAILABLE:
      case SlotRequestStatus.SLOTS_PARTIALLY_ASSIGNED:
      case SlotRequestStatus.SLOTS_ASSIGNED:
      case OrderStatus.CONFIRMED:
      case SlotStatus.ASSIGNED:
      case SuppliesRequestStatus.ORDER_CREATED:
        return { bgColor: AppColors.LIGHT_GREEN, label: status };
      case CashRequestStatus.TO_PROCESS:
      case CheckRequestStatus.TO_PROCESS:
      case TransferRequestStatus.PENDING:
      case SellOrderStatus.TO_PROCESS:
      case SlotRequestStatus.PENDING:
      case SuppliesRequestStatus.PENDING_CONFIRMATION:
      case SuppliesRequestStatus.IN_TRANSIT:
      case SuppliesOrderStatus.PENDING:
      case SuppliesOrderStatus.IN_TRANSIT:
      case SlotStatus.PENDING:
      case SlotStatus.TRANSIT:
        return { bgColor: AppColors.YELLOW_SECONDARY, label: status };
      case OrderStatus.TRANSIT:
        return { bgColor: AppColors.YELLOW_SECONDARY, label: "En tránsito" };
      case SuppliesRequestStatus.PARTIAL_DELIVERY:
          return { bgColor: AppColors.LIGHT_GREEN, label: "Entrega parcial" };
      case CashRequestStatus.NEEDS_INFO:
      case CheckRequestStatus.NEEDS_INFO:
      case SlotRequestStatus.NEEDS_INFO:
      case SuppliesRequestStatus.NEEDS_INFO:
        return {
          bgColor: AppColors.BLACK_TEXT_SECONDARY,
          label: "Requiere información",
        };
      case SellOrderStatus.IN_COMMUNICATION:
      case CounterDeliveryStatus.IN_COMMUNICATION:
      case FutureContractStatus.IN_COMMUNICATION:
        return {
          bgColor: AppColors.BLACK_TEXT_SECONDARY,
          label: "En comunicación",
        };
      case SellOrderStatus.NEGOTIATION:
      case CounterDeliveryStatus.NEGOTIATION:
      case FutureContractStatus.NEGOTIATION:
        return {
          bgColor: AppColors.BLACK_TEXT_SECONDARY,
          label: "Negociación",
        };
      case CashRequestStatus.REJECTED:
      case CheckRequestStatus.REJECTED:
      case TransferRequestStatus.REJECTED:
      case TransferRequestStatus.FAILED:
      case ScheduledTransferStatus.REJECTED:
      case ScheduledTransferStatus.FAILED:
      case SellOrderStatus.REJECTED:
      case SuppliesRequestStatus.REJECTED:
        return { bgColor: AppColors.RED_ICON, label: status };
      case CashRequestStatus.FINISHED:
      case CheckRequestStatus.FINISHED:
      case TransferRequestStatus.FINISHED:
      case ScheduledTransferStatus.FINISHED:
      case SellOrderStatus.FINISHED:
      case SellOrderStatus.CONFIRMED:
      case CounterDeliveryStatus.CONFIRMED:
      case FutureContractStatus.CONFIRMED:
      case OrderStatus.DELIVERED:
      case BackupCheckStatus.FINISHED:
      case SuppliesRequestStatus.PARTIALLY_CONFIRMED:
      case SuppliesRequestStatus.ORDER_CONFIRMED:
      case SuppliesRequestStatus.DELIVERED:
      case SuppliesOrderStatus.CONFIRMED:
      case SuppliesOrderStatus.DELIVERED:
      case SlotStatus.DELIVERED:
        return { bgColor: AppColors.GREEN_PRIMARY, label: status };
      case ScheduledTransferStatus.SCHEDULED:
        return { bgColor: AppColors.YELLOW_ALT, label: status };
      case ScheduledTransferStatus.ON_REVISION:
        return { bgColor: AppColors.RED_ICON_ALT, label: "En revisión" };
      case BackupCheckStatus.PENDING_CHECK:
        return {
          bgColor: AppColors.YELLOW_SECONDARY,
          label: "Pendiente de cheque",
        };
      case BackupCheckStatus.PENDING_ENDOSE:
        return {
          bgColor: AppColors.GREEN_SECONDARY,
          label: "Pendiente de endoso",
        };
      case SellOrderStatus.CANCELLED:
      case CounterDeliveryStatus.CANCELLED:
      case FutureContractStatus.CANCELLED:
      case BackupCheckStatus.CANCELLED:
      case SuppliesRequestStatus.CANCELLED:
        return { bgColor: AppColors.ERROR_TEXT, label: status };
      default:
        return { bgColor: "transparent", label: status };
    }
  }, [status]);

  if (!status) return null;

  return (
    <LightMode>
      <Badge bgColor={bgColor}>
        <Text
          paddingY={noPaddingY ? "" : "4px"}
          paddingX="8px"
          fontSize="12px"
          color={AppColors.WHITE_SECONDARY}
          data-testid="request-status-badge-text"
        >
          {label}
        </Text>
      </Badge>
    </LightMode>
  );
};
